import React from "react";
import { fileUrl } from "../lib/api";

/**
 * Zone positions calibrated for the Panther jersey mockups served from /brand/jersey-recto.png
 * and /brand/jersey-verso.png (2048×2048 each).
 * Percentages are anchored to the image container. Adjust here if the jersey artwork changes.
 */
const ZONES = {
    // FRONT — Panther recto v2
    poitrine:      { label: "Poitrine",   side: "front", style: "top-[50%] left-[34%] w-[32%] h-[12%]" },
    cou_droit:     { label: "Cou D.",     side: "front", style: "top-[14%] right-[20%] w-[16%] h-[7%]" },
    cou_gauche:    { label: "Cou G.",     side: "front", style: "top-[14%] left-[20%] w-[16%] h-[7%]" },
    // BACK — Panther verso v2
    dos_haut:      { label: "Dos haut",   side: "back",  style: "top-[12%] left-[30%] w-[36%] h-[10%]" },
    dos_bas:       { label: "Dos bas",    side: "back",  style: "top-[68%] left-[30%] w-[36%] h-[9%]" },
    epaule_gauche: { label: "Épaule G.",  side: "back",  style: "top-[36%] right-[8%] w-[18%] h-[9%]" },
};

const JERSEY_SRC = {
    front: "/brand/jersey-recto.png",
    back: "/brand/jersey-verso.png",
};

function JerseySide({
    side, label, prices, selected, onToggle,
    sponsorships, selectable, showPrices = true, previewLogo, majorColor,
}) {
    const occupiedByZone = {};
    sponsorships.forEach((s) => {
        s.zones?.forEach((z) => {
            occupiedByZone[z.name] = s;
        });
    });

    const jerseyOnProduction = sponsorships.some((s) =>
        ["in_production", "delivered"].includes(s.jersey_status || "")
    );
    const revealOccupiedLogo = jerseyOnProduction;

    return (
        <div className="flex flex-col">
            <div className="text-center text-[11px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-3">
                {label}
            </div>
            <div
                className="relative w-full max-w-[640px] mx-auto aspect-square bg-gradient-radial-black border border-zinc-800 overflow-hidden"
                data-testid={`jersey-${side}`}
                style={{
                    background:
                        "radial-gradient(ellipse at center, #1a1a1a 0%, #050505 70%)",
                }}
            >
                <img
                    src={JERSEY_SRC[side]}
                    alt={`Maillot ${label.toLowerCase()}`}
                    className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
                    draggable={false}
                />

                {Object.entries(ZONES)
                    .filter(([, def]) => def.side === side)
                    .map(([key, def]) => {
                        const isSelected = selected.includes(key);
                        const occupied = occupiedByZone[key];
                        const price = prices?.[key];
                        const interactive = selectable && !occupied;

                        // What to show inside the zone
                        const showLogo = occupied && revealOccupiedLogo && occupied.logo_path;
                        const showPreview = isSelected && previewLogo;

                        return (
                            <button
                                type="button"
                                key={key}
                                disabled={!interactive}
                                data-testid={`zone-${key}`}
                                onClick={() => interactive && onToggle?.(key)}
                                className={[
                                    "absolute group", def.style,
                                    "border-2 transition-all flex items-center justify-center rounded-sm",
                                    occupied
                                        ? "border-solid border-zinc-500/80 bg-white/5 backdrop-blur-[1px]"
                                        : isSelected
                                            ? "border-solid border-orange-500 bg-orange-500/20 shadow-[0_0_24px_rgba(234,88,12,0.65)]"
                                            : "border-dashed border-zinc-200/70 bg-zinc-900/30 hover:border-orange-500 hover:bg-orange-500/25 cursor-pointer",
                                ].join(" ")}
                            >
                                {showLogo ? (
                                    <img
                                        src={fileUrl(occupied.logo_path)}
                                        alt={occupied.sponsor_company || "Sponsor"}
                                        className="max-w-[90%] max-h-[90%] object-contain"
                                    />
                                ) : showPreview ? (
                                    <img
                                        src={previewLogo}
                                        alt="Votre logo"
                                        className="max-w-[90%] max-h-[90%] object-contain opacity-95"
                                    />
                                ) : occupied ? (
                                    <div className="text-center px-1">
                                        <div className="text-[8px] uppercase tracking-widest text-zinc-700 font-bold leading-tight">
                                            Emplacement
                                        </div>
                                        <div className="text-[8px] uppercase tracking-widest text-zinc-700 font-bold leading-tight">
                                            réservé
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center px-1">
                                        <div className="text-[9px] uppercase tracking-widest text-zinc-900 font-bold drop-shadow-[0_0_2px_rgba(255,255,255,0.6)] whitespace-nowrap">
                                            {def.label}
                                        </div>
                                        {showPrices && price !== undefined && (
                                            <div className={[
                                                "font-display leading-none mt-0.5",
                                                "text-base sm:text-lg md:text-xl",
                                                isSelected ? "text-white" : "text-orange-600",
                                            ].join(" ")}>{price}€</div>
                                        )}
                                    </div>
                                )}
                            </button>
                        );
                    })}
            </div>
            {majorColor && (
                <div
                    className="flex items-center justify-center gap-2 mt-3 text-[11px] uppercase tracking-[0.2em] text-zinc-300"
                    data-testid={`jersey-major-color-${side}`}
                >
                    <span
                        className="w-4 h-4 rounded-full border border-zinc-600"
                        style={{ background: majorColor.hex }}
                    />
                    Couleur dominante : <span className="text-zinc-100 font-bold">{majorColor.name}</span>
                </div>
            )}
        </div>
    );
}

export default function JerseyConfigurator({
    prices, selected = [], onToggle, sponsorships = [],
    selectable = true, showPrices = true, previewLogo, majorColor,
}) {
    return (
        <div className="flex flex-col gap-8">
            <JerseySide
                side="front"
                label="Avant"
                prices={prices} selected={selected} onToggle={onToggle}
                sponsorships={sponsorships} selectable={selectable} showPrices={showPrices}
                previewLogo={previewLogo} majorColor={majorColor}
            />
            <JerseySide
                side="back"
                label="Arrière"
                prices={prices} selected={selected} onToggle={onToggle}
                sponsorships={sponsorships} selectable={selectable} showPrices={showPrices}
                previewLogo={previewLogo} majorColor={majorColor}
            />
        </div>
    );
}

export const ZONE_KEYS = Object.keys(ZONES);
export const ZONE_LABELS = Object.fromEntries(
    Object.entries(ZONES).map(([k, v]) => [k, v.label])
);
export const ZONE_SIDE = Object.fromEntries(
    Object.entries(ZONES).map(([k, v]) => [k, v.side])
);
